import React, { useState, useEffect, useCallback } from "react";
import {
    getDashboardMetrics,
    getPaginatedUsers,
} from "./actions";
import { DashboardShell } from "../campaign-renewals/DashboardShell";
import { DashboardHeader } from "../campaign-renewals/DashboardHeader";
import { UserQuotaMetricCards } from "./UserQuotaMetricCards";
import { UserQuotaList } from "./UserQuotaList";
import { UserQuotaMetrics, PaginatedUserQuotaData } from "./types";
import LoadingScreen from "../../screens/LoadingScreen";
import { useIsMobile } from "../../hooks/useIsMobile";

const UserQuota = () => {
    const mobile = useIsMobile(900);
    const [usersPage, setUsersPage] = useState(1);
    const [limit] = useState(10);
    const [metrics, setMetrics] = useState<UserQuotaMetrics | null>(null);
    const [error, setError] = useState(false);
    const [users, setUsers] = useState<PaginatedUserQuotaData>();
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState<string | undefined>();
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [loading, setLoading] = useState(false);

    const fetchMetrics = async () => {
        try {
            const data = await getDashboardMetrics();
            setMetrics(data);
        } catch (error) {
            console.error("Failed to fetch dashboard metrics:", error);
            setError(true);
        }
    };

    const fetchUsers = useCallback(
        async (p: number = usersPage) => {
            try {
                setLoading(true);
                const data = await getPaginatedUsers(
                    p,
                    limit,
                    searchTerm,
                    sortBy,
                    sortDirection
                );
                setUsers(data);
            } catch (error) {
                console.error("Failed to fetch users:", error);
                setError(true);
            } finally {
                setLoading(false);
            }
        },
        [limit, usersPage, searchTerm, sortBy, sortDirection]
    );

    useEffect(() => {
        fetchMetrics();
    }, []);

    useEffect(() => {
        fetchUsers(usersPage);
    }, [usersPage, fetchUsers]);

    const handleSortChange = (newSortBy: string, newSortDirection: "asc" | "desc") => {
        setSortBy(newSortBy);
        setSortDirection(newSortDirection);
        setUsersPage(1); // Reset to first page when sorting changes
    };

    if (error) return <div>Error loading metrics</div>;
    if (!metrics) return <LoadingScreen />;

    return (
        <div className={mobile ? "p-4" : "p-6"}>
            <DashboardShell>
                <DashboardHeader
                    heading="User Quota Dashboard"
                    text="Monitor user resource usage and quotas"
                />

                {/* Parent grid: single column on mobile, 6 columns on large */}
                <div
                    className={
                        mobile
                            ? "grid gap-6 grid-cols-1"
                            : "grid gap-6 grid-cols-1 lg:grid-cols-6"
                    }
                >
                    {/* Metric cards: full width, horizontally scrollable on mobile */}
                    <div className="col-span-full">
                        <div className={mobile ? "overflow-x-auto" : ""}>
                            <UserQuotaMetricCards metrics={metrics} isMobile={mobile} />
                        </div>
                    </div>

                    {/* Users list */}
                    <UserQuotaList
                        users={users?.data || []}
                        page={usersPage}
                        setPage={setUsersPage}
                        limit={limit}
                        total={users?.total || 0}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        isMobile={mobile}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        loading={loading}
                    />
                </div>
            </DashboardShell>
        </div>
    );
};

export default UserQuota;